import { useReactiveVar } from '@apollo/client';
import React from 'react';
import { SettingsBase } from 'settings';
import { SuppliersList } from 'settings/account/supplier/list';
import { setUserPrivileges } from 'utility/cache/ui';
import { TravelPolicyList } from './TravelPolicy/list';
import { TravelersList } from './Travelers/list';
import { AccountsEntryList } from './accountsEntry/list';
import { ApprovalSetup } from './approvalSetup/list';
import { BusinessRules } from './businessRules';
import { BusinessUnitsList } from './businessUnits/list';
import { CompanyMaintenance } from './companyMaintenance';
import { CorporateAccountsList } from './corporateAccounts/list';
import { CorporateWorkGroupsList } from './corporateWorkGroups/list';
import { DataAccessPolicyList } from './dataAccessPolicy/list';
import { DepartmentBudget } from './departmentBudget/list';
import { DepartmentGroupsList } from './departmentGroups/list';
import { DepartmentsList } from './departments/list';
import { ApprovalSetupFlowList } from './intuitiveApprovalSetupFlow/list';
import { OccupationsList } from './occupations/list';
import { UserAccessPoolsList } from './userAccessPools/list';
import { UserGroupsList } from './userGroups/list';
import { UserSetup } from './userSetup/list';
import { UserTemplatesList } from './userTemplates/list';
import { WorkgroupsGroupList } from './workgroupGroups/list';
import { filterSectionsByPrivilege, SettingsOption } from 'settings/utils';
import { NotificationCycles } from './notificationCycles/list';

const ACCOUNT_PATH = '/account-management';
export const sections: SettingsOption[] = [
  // approval-setup navigates to intuitive approval flow setup
  {
    name: 'Approval Flow',
    path: `${ACCOUNT_PATH}/approval-setup`,
    component: ApprovalSetupFlowList,
    routeName: 'APPROVAL_WORKFLOW_DESIGNER',
    icon: 'Org',
  },
  {
    name: 'Approval Rules',
    path: `${ACCOUNT_PATH}/approvals`,
    component: ApprovalSetup,
    routeName: 'APPROVAL_SETUP',
    icon: 'Org',
  },
  {
    name: 'Suppliers',
    path: `${ACCOUNT_PATH}/suppliers`,
    component: SuppliersList,
    routeName: 'SUPPLIER',
    icon: 'Contact',
  },

  // Travel Group
  {
    name: 'Travel',
    icon: 'Airplane',
    children: [
      {
        name: 'Travelers',
        path: `${ACCOUNT_PATH}/travelers`,
        component: TravelersList,
        routeName: 'TRAVELER',
        icon: 'Group',
      },
      {
        name: 'Travel Policies',
        path: `${ACCOUNT_PATH}/travel-policy`,
        component: TravelPolicyList,
        routeName: 'TRAVEL_POLICIES',
        icon: 'Airplane',
      },
    ],
  },

  // Account settings group
  {
    name: 'Account Settings',
    icon: 'AccountManagement',
    children: [
      {
        name: 'Accounts',
        path: `${ACCOUNT_PATH}/accounts`,
        component: AccountsEntryList,
        routeName: 'LOOKUP_ACCOUNTS',
        icon: 'AccountManagement',
      },
      {
        name: 'Budgeting',
        path: `${ACCOUNT_PATH}/department-budget`,
        component: DepartmentBudget,
        routeName: 'DEPARTMENT_BUDGET_SETUP',
        icon: 'Calculator',
      },
      {
        name: 'Corporate Accounts',
        path: `${ACCOUNT_PATH}/corporate-accounts`,
        component: CorporateAccountsList,
        routeName: 'LOOKUP_CORPORATE_ACCOUNTS',
        icon: 'AccountBrowser',
      },
    ],
  },

  // Department settings group
  {
    name: 'Department Settings',
    icon: 'Org',
    children: [
      {
        name: 'Departments',
        path: `${ACCOUNT_PATH}/departments`,
        component: DepartmentsList,
        routeName: 'DEPARTMENT_SETUP',
        icon: 'Org',
      },
      {
        name: 'Department Groups',
        path: `${ACCOUNT_PATH}/department-groups`,
        component: DepartmentGroupsList,
        routeName: 'DEPARTMENT_GROUP_SETUP',
        icon: 'RowsGroup',
      },
    ],
  },

  // User Settings group
  {
    name: 'User Settings',
    icon: 'Group',
    children: [
      {
        name: 'Users',
        path: `${ACCOUNT_PATH}/users`,
        component: UserSetup,
        routeName: 'USER_SETUP',
        icon: 'Group',
      },
      {
        name: 'Occupations',
        path: `${ACCOUNT_PATH}/occupations`,
        component: OccupationsList,
        routeName: 'OCCUPATION_SETUP',
        icon: 'Toolbox',
      },
      {
        name: 'User Groups',
        path: `${ACCOUNT_PATH}/user-groups`,
        component: UserGroupsList,
        routeName: 'USER_GROUPS',
        icon: 'AddGroup',
      },
      {
        name: 'User Access Pool',
        path: `${ACCOUNT_PATH}/user-access`,
        component: UserAccessPoolsList,
        routeName: 'ACCESS_GROUPS',
        icon: 'PageLock',
      },
      {
        name: 'Data Access Policy',
        path: `${ACCOUNT_PATH}/data-access-policy`,
        component: DataAccessPolicyList,
        routeName: 'DATA_ACCESS_POLICIES',
        icon: 'PageLock',
      },
      {
        name: 'User Templates',
        path: `${ACCOUNT_PATH}/userTemplates`,
        component: UserTemplatesList,
        routeName: 'DEPARTMENT_OCC_TEMPLATE',
        icon: 'Group',
      },
    ],
  },

  // Company Settings group
  {
    name: 'Company Settings',
    icon: 'BusinessCenterLogo',
    children: [
      {
        name: 'Company',
        path: `${ACCOUNT_PATH}/company-maintenance`,
        component: CompanyMaintenance,
        routeName: 'COMPANY_INFO',
        icon: 'BusinessCenterLogo',
      },
      {
        name: 'Business Units',
        path: `${ACCOUNT_PATH}/business-units`,
        component: BusinessUnitsList,
        routeName: 'COMPANY_BUSINESS_UNITS',
        icon: 'BusinessCenterLogo',
      },
      {
        name: 'Workgroups',
        path: `${ACCOUNT_PATH}/work-groups`,
        component: CorporateWorkGroupsList,
        routeName: 'CORPORATE_WORKGROUPS',
        icon: 'WorkforceManagement',
      },
      {
        name: 'Workgroup Groups',
        path: `${ACCOUNT_PATH}/workgroups-groups`,
        component: WorkgroupsGroupList,
        routeName: 'WORKGROUP_GROUPS',
        icon: 'HomeGroup',
      },
      {
        name: 'Notification Cycles',
        path: `${ACCOUNT_PATH}/notification-cycles`,
        component: NotificationCycles,
        routeName: 'NOTIFICATION_TIMES',
        icon: 'TimeEntry',
      },
      {
        name: 'Business Rules',
        path: `${ACCOUNT_PATH}/business-rules`,
        component: BusinessRules,
        routeName: 'COMPANY_BUSINESS_RULES',
        icon: 'BusinessHoursSign',
      },
    ],
  },
];

export const AccountManagement: React.FC = () => {
  const privileges = useReactiveVar(setUserPrivileges);
    const sectionsList = filterSectionsByPrivilege(sections, privileges);
  

  return (
    <SettingsBase
      basePath={ACCOUNT_PATH}
      sections={sectionsList}
      title="Account Management"
    />
  );
};
